'use client';

import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import { fetchRecruiterCallVoiceToken } from '@hooks/useSwrApi/endpoints/recruiter-call';
import type { RecruiterCallConnectParams } from '@type/recruiter-call';

export type TwilioDeviceState = 'idle' | 'registering' | 'ready' | 'connecting' | 'in-call' | 'error';

interface UseTwilioDeviceReturn {
  state: TwilioDeviceState;
  error: string | null;
  callSid: string | null;
  isMuted: boolean;
  callDuration: number;
  connect: (params: RecruiterCallConnectParams) => Promise<void>;
  disconnect: () => void;
  toggleMute: () => void;
}

/**
 * Bootstraps a Twilio.Device for the logged-in recruiter and exposes the
 * softphone lifecycle as React state.
 *
 * The Device is created lazily on first `connect()` and torn down on
 * component unmount so we don't keep an idle WebRTC session open per
 * candidate profile tab.
 */
export const useTwilioDevice = (): UseTwilioDeviceReturn => {
  const [state, setState] = useState<TwilioDeviceState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearDurationTimer();
    if (callRef.current) {
      callRef.current.disconnect();
      callRef.current = null;
    }
    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null;
    }
    setCallSid(null);
    setIsMuted(false);
    setCallDuration(0);
  }, [clearDurationTimer]);

  useEffect(() => teardown, [teardown]);

  const ensureDevice = useCallback(async (): Promise<Device> => {
    if (deviceRef.current) {
      return deviceRef.current;
    }
    setState('registering');
    const { token } = await fetchRecruiterCallVoiceToken();
    if (!token) {
      throw new Error('RecruiterCall: voice-token endpoint returned no token');
    }
    const device = new Device(token, {
      // logLevel: 'debug', // enable when debugging WebRTC issues
    });
    device.on('error', (twilioError: Error) => {
      setError(twilioError.message);
      setState('error');
    });
    await device.register();
    deviceRef.current = device;
    setState('ready');
    return device;
  }, []);

  const attachCallListeners = useCallback((call: Call) => {
    call.on('accept', () => {
      setState('in-call');
      setCallSid(call.parameters.CallSid ?? null);
      const startedAt = Date.now();
      clearDurationTimer();
      durationTimerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - startedAt) / 1000));
      }, 1000);
    });
    call.on('disconnect', () => {
      clearDurationTimer();
      callRef.current = null;
      setState('ready');
    });
    call.on('cancel', () => {
      clearDurationTimer();
      callRef.current = null;
      setState('ready');
    });
    call.on('error', (twilioError: Error) => {
      clearDurationTimer();
      setError(twilioError.message);
      setState('error');
    });
    call.on('mute', (muted: boolean) => setIsMuted(muted));
  }, [clearDurationTimer]);

  const connect = useCallback(async (params: RecruiterCallConnectParams) => {
    setError(null);
    try {
      const device = await ensureDevice();
      setState('connecting');
      const call = await device.connect({ params });
      callRef.current = call;
      attachCallListeners(call);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    }
  }, [attachCallListeners, ensureDevice]);

  const disconnect = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!callRef.current) return;
    const next = !callRef.current.isMuted();
    callRef.current.mute(next);
  }, []);

  return {
    state,
    error,
    callSid,
    isMuted,
    callDuration,
    connect,
    disconnect,
    toggleMute,
  };
};
